import React from 'react';

import { Divider } from '../../components/Divider/Divider';
import { FinalGridContainer, FinalGridContainerProps, FinalGridItem } from '../../components/FinalGrid/FinalGrid';

declare module '@mui/material/Grid' {}

export interface TeaserGalleryProps extends FinalGridContainerProps {
  children?: React.ReactNode;
  id?: string;
}

const getColumnsWidth = (variant: string): 12 | 4 | 6 | 3 => {
  switch (variant) {
    case 'horizontalLarge':
    case 'content:heroThirdLeft':
    case 'content:heroThirdRight':
    case 'contentExtraLarge':
    case 'content:heroHalfLeft':
    case 'content:heroHaltRight':
    case 'product:heroThirdLeft':
    case 'product:heroThirdRight':
    case 'contentLarge':
    case 'campaign':
    case 'content:recommendation':
    case 'content:list':
    case 'service:list':
    case 'service:campaign':
    case 'service:heroMediaLeft':
    case 'service:heroMediaRight':
    case 'service:heroMediaSquare':
      return 12;
    case 'content:oneHalf':
    case 'content:small':
    case 'product:half':
    case 'product:halfSquare':
    case 'service:cardHalf':
      return 6;
    case 'info':
    case 'seminar':
    case 'content:oneThird':
    case 'product:third':
    case 'product:thirdSquare':
      return 4;
    case 'product:fourth':
      return 3;
    default:
      return 12;
  }
};

const getChildVariant = (child: React.ReactElement): string => {
  if (typeof child.props?.variant === 'string') {
    return child.props.variant;
  }

  // Called via Consumer
  if (typeof child.props?.node !== 'undefined') {
    // __component === teaser.teaser
    if (typeof child.props?.options?.variant !== 'undefined') {
      return child.props.options.variant;
    }
    // __component === teaser.infoTeaser
    if (typeof child.props.node.infoCard !== 'undefined') {
      return 'info';
    }
  }

  return 'unknown';
};

const renderChildren = ({ children, id }: TeaserGalleryProps): React.ReactElement[] => {
  return React.Children.toArray(children).reduce(
    (accumulator: { currentWidth: number; result: React.ReactElement[] }, child: React.ReactNode) => {
      if (!React.isValidElement(child)) {
        return accumulator;
      }
      const width = getColumnsWidth(getChildVariant(child));

      accumulator.result.push(
        <FinalGridItem
          xs={12}
          sm={getChildVariant(child) === 'product:fourth' ? 6 : width}
          md={width}
          lg={width}
          key={`${id}.teaser.${child.key}`}
          id={`${id}.teaser.${child.key}`}
        >
          {child}
        </FinalGridItem>
      );

      if (accumulator.currentWidth + width >= 12) {
        accumulator.result.push(
          <Divider
            key={`${id}.divider.${accumulator.result.length}`}
            id={`${id}.divider.${accumulator.result.length}`}
          />
        );

        return { currentWidth: 0, result: accumulator.result };
      }

      return { currentWidth: accumulator.currentWidth + width, result: accumulator.result };
    },
    { currentWidth: 0, result: [] }
  ).result;
};

/**
 * The Teaser
 *
 * @version [AIV]{version}[/AIV]
 * @author [SFP](https://gitlab.sfp.berlin/products/sparkasse.de/portals)
 * @see [https://styleguide.sparkasse.de/document/69#/collection-typen/serviceteasergallery]
 */
export const TeaserGallery: React.FC<TeaserGalleryProps> = ({ children, id }: TeaserGalleryProps) => {
  return (
    <FinalGridContainer rowGap={{ xs: 8, sm: 8, md: 8, lg: 9 }}>{renderChildren({ children, id })}</FinalGridContainer>
  );
};
